import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Shield, CheckCircle2, Eye, Clock, XCircle, Search,
  User, Heart, MessageSquare,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'sonner';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';
import { INCIDENTES } from '../../api/routes';
import type { ChatDesktopNavState } from '../admin/ChatDesktop';

interface Incidente {
  id: string;
  tipoIncidente: string;
  severidade: string;
  status: string;
  titulo: string;
  descricao: string;
  criadoEm: string;
  resolvidoEm?: string;
  resolvidoPor?: { nome: string };
  aluno?: { id: string; nome: string; matricula?: string };
  turma?: { nome: string };
  presencaDiaria?: { dataReferencia: string };
}

const SEV_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  CRITICA:   { label: 'Crítica', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  ALTA:      { label: 'Alta', color: '#f97316', bg: 'rgba(249,115,22,0.1)' },
  MEDIA:     { label: 'Média', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  BAIXA:     { label: 'Baixa', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
  INFO:      { label: 'Info', color: '#6b7280', bg: 'rgba(107,114,128,0.1)' },
};

const STATUS_CONFIG: Record<string, { label: string; icon: any; color: string }> = {
  ABERTO:     { label: 'Aberto', icon: <AlertTriangle size={14} />, color: '#ef4444' },
  EM_ANALISE: { label: 'Em Análise', icon: <Eye size={14} />, color: '#f59e0b' },
  RESOLVIDO:  { label: 'Resolvido', icon: <CheckCircle2 size={14} />, color: '#22c55e' },
  FECHADO:    { label: 'Fechado', icon: <XCircle size={14} />, color: '#6b7280' },
};

function formatIncidenteDraft(inc: Incidente): string {
  const sev = SEV_CONFIG[inc.severidade]?.label || inc.severidade || '—';
  const tipo = (inc.tipoIncidente || 'INCIDENTE').replace(/_/g, ' ');
  const quando = (() => {
    try {
      return new Date(inc.criadoEm).toLocaleString('pt-BR');
    } catch {
      return '—';
    }
  })();
  const linhas = [
    `Olá! Entramos em contato sobre o seguinte incidente:`,
    ``,
    `• Tipo: ${tipo}`,
    `• Severidade: ${sev}`,
    `• Aluno: ${inc.aluno?.nome || '—'}`,
    inc.turma?.nome ? `• Turma: ${inc.turma.nome}` : null,
    `• Quando: ${quando}`,
    inc.titulo ? `• Título: ${inc.titulo}` : null,
    inc.descricao ? `• Detalhe: ${inc.descricao}` : null,
    ``,
    `Podemos conversar sobre a situação?`,
  ].filter(Boolean);
  return linhas.join('\n');
}

export function IncidentesPage() {
  const user = useAuthStore((s) => s.user);
  const escolaId = user?.escolaId || '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filtroStatus, setFiltroStatus] = useState<string>('ABERTO');
  const [busca, setBusca] = useState('');
  const [abrindoResp, setAbrindoResp] = useState<string | null>(null);
  const [abrindoChat, setAbrindoChat] = useState<string | null>(null);

  const { data: incidentes = [], isLoading } = useQuery({
    queryKey: ['incidentes', escolaId, filtroStatus],
    queryFn: async () => {
      const { data } = await api.get<Incidente[]>(INCIDENTES.porEscola(escolaId), {
        params: { status: filtroStatus || undefined },
      });
      return Array.isArray(data) ? data : (data as any)?.items || [];
    },
    enabled: !!escolaId,
  });

  const abrirAluno = (alunoId: string) => {
    navigate(`/equipe/alunos/${alunoId}`);
  };

  /** Abre a ficha do aluno na aba Responsável. */
  const abrirResponsavel = async (alunoId: string) => {
    if (!escolaId || !alunoId) return;
    setAbrindoResp(alunoId);
    try {
      const { data } = await api.get(ROUTES.alunos.findOne(escolaId, alunoId));
      const lista = (data as any)?.responsaveis;
      const temResp = Array.isArray(lista) && lista.some((ra: any) => ra?.responsavel?.id || ra?.responsavelId);
      if (!temResp) {
        toast.message('Aluno sem responsável vinculado — abrindo ficha do aluno.');
      }
      navigate(`/equipe/alunos/${alunoId}?tab=responsavel`);
    } catch {
      toast.error('Não foi possível carregar o aluno');
    } finally {
      setAbrindoResp(null);
    }
  };

  /**
   * Resolver = Atendimento (Pais): abre/cria conversa com responsável que tem app
   * e pré-preenche a mensagem com os dados do incidente (push ao enviar).
   */
  const abrirChatIncidente = async (inc: Incidente) => {
    if (!escolaId) return;
    if (!inc.aluno?.id) {
      toast.error('Incidente sem aluno vinculado');
      return;
    }
    setAbrindoChat(inc.id);
    try {
      const { data: aluno } = await api.get(ROUTES.alunos.findOne(escolaId, inc.aluno.id));
      const lista: any[] = Array.isArray((aluno as any)?.responsaveis)
        ? (aluno as any).responsaveis
        : [];
      const comApp = lista.find((ra) => ra?.responsavel?.usuarioId && ra?.responsavel?.id);
      const qualquer = lista.find((ra) => ra?.responsavel?.id);
      const ra = comApp || qualquer;
      if (!ra?.responsavel?.id) {
        toast.error('Aluno sem responsável vinculado');
        return;
      }
      if (!ra.responsavel.usuarioId) {
        toast.error('Responsável sem app instalado. Convide o responsável primeiro.');
        navigate(`/equipe/alunos/${inc.aluno.id}?tab=responsavel`);
        return;
      }

      const { data: conversa } = await api.post(
        `/operacao/conversas/escola/${escolaId}/abrir`,
        { responsavelId: ra.responsavel.id, alunoId: inc.aluno.id },
      );

      const state: ChatDesktopNavState = {
        conversaId: conversa.id,
        draftText: formatIncidenteDraft(inc),
        seedConversa: {
          id: conversa.id,
          alunoId: conversa.alunoId,
          responsavelId: conversa.responsavelId,
          professorId: conversa.professorId ?? null,
          ultimoTexto: conversa.ultimoTexto ?? null,
          criadoEm: conversa.criadoEm,
          atualizadoEm: conversa.atualizadoEm,
          aluno: conversa.aluno,
          responsavel: conversa.responsavel,
          escola: conversa.escola,
          unreadCount: 0,
        },
      };
      navigate('/equipe/conversas-pais', { state });
      void queryClient.invalidateQueries({ queryKey: ['incidentes'] });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Não foi possível abrir o chat com o responsável';
      toast.error(Array.isArray(msg) ? msg[0] : String(msg));
    } finally {
      setAbrindoChat(null);
    }
  };

  const filtrados = useMemo(() => {
    if (!busca) return incidentes;
    const q = busca.toLowerCase();
    return incidentes.filter((i: Incidente) =>
      i.titulo?.toLowerCase().includes(q) ||
      i.aluno?.nome?.toLowerCase().includes(q) ||
      i.tipoIncidente?.toLowerCase().includes(q)
    );
  }, [incidentes, busca]);

  const stats = useMemo(() => {
    return {
      abertos: incidentes.filter((i: Incidente) => i.status === 'ABERTO').length,
      emAnalise: incidentes.filter((i: Incidente) => i.status === 'EM_ANALISE').length,
      total: incidentes.length,
    };
  }, [incidentes]);

  const btnGhost: React.CSSProperties = {
    flex: 1,
    padding: '6px 10px',
    borderRadius: 8,
    fontSize: '0.72rem',
    fontWeight: 600,
    background: 'transparent',
    color: 'var(--text-main)',
    border: '1px solid var(--border-color)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Incidentes Operacionais</h1>
          <p>Gestão de alertas e incidentes da escola</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div className="kpi-card hover-lift" style={{ gap: 6 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#ef4444' }}>Abertos</span>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ef4444' }}>{stats.abertos}</span>
        </div>
        <div className="kpi-card hover-lift" style={{ gap: 6 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#f59e0b' }}>Em Análise</span>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f59e0b' }}>{stats.emAnalise}</span>
        </div>
        <div className="kpi-card hover-lift" style={{ gap: 6 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Total Listado</span>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-main)' }}>{stats.total}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {['ABERTO', 'EM_ANALISE', 'RESOLVIDO', ''].map(s => {
          const cfg = STATUS_CONFIG[s] || { label: 'Todos', color: '#6b7280' };
          return (
            <button
              key={s}
              onClick={() => setFiltroStatus(s)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600,
                border: '1px solid var(--border-color)', cursor: 'pointer',
                background: filtroStatus === s ? cfg.color : 'transparent',
                color: filtroStatus === s ? '#fff' : cfg.color,
                borderColor: filtroStatus === s ? cfg.color : undefined,
              }}
            >
              {cfg.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Buscar..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            style={{
              padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid var(--border-color)',
              background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.82rem', width: 200,
            }}
          />
        </div>
      </div>

      <div style={cardStyle}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Carregando incidentes...</div>
        ) : filtrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>
            <Shield size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
            <p style={{ margin: 0 }}>Nenhum incidente encontrado</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtrados.map((inc: Incidente) => {
              const sev = SEV_CONFIG[inc.severidade] || SEV_CONFIG.INFO;
              const st = STATUS_CONFIG[inc.status] || STATUS_CONFIG.ABERTO;
              return (
                <div key={inc.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
                  borderRadius: 10, background: sev.bg, border: `1px solid ${sev.color}20`,
                }}>
                  <div style={{ color: st.color, marginTop: 2 }}>{st.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main)', marginBottom: 2 }}>
                      {inc.titulo || inc.tipoIncidente}
                    </div>
                    {inc.descricao && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                        {inc.descricao.length > 100 ? inc.descricao.slice(0, 100) + '...' : inc.descricao}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: '0.65rem' }}>
                      <span style={{ padding: '1px 6px', borderRadius: 4, background: sev.bg, color: sev.color, fontWeight: 700 }}>
                        {sev.label}
                      </span>
                      <span style={{ padding: '1px 6px', borderRadius: 4, background: `${st.color}15`, color: st.color, fontWeight: 700 }}>
                        {st.label}
                      </span>
                      {inc.aluno && (
                        <span style={{ color: 'var(--text-muted)' }}>
                          👤 {inc.aluno.nome}
                        </span>
                      )}
                      {inc.turma && <span style={{ color: 'var(--text-muted)' }}>📚 {inc.turma.nome}</span>}
                      <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Clock size={10} /> {new Date(inc.criadoEm).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 168 }}>
                    {inc.aluno?.id && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" onClick={() => abrirAluno(inc.aluno!.id)} style={btnGhost}>
                          <User size={12} /> Aluno
                        </button>
                        <button
                          type="button"
                          onClick={() => void abrirResponsavel(inc.aluno!.id)}
                          disabled={abrindoResp === inc.aluno.id}
                          style={{ ...btnGhost, opacity: abrindoResp === inc.aluno.id ? 0.6 : 1 }}
                        >
                          <Heart size={12} /> Responsável
                        </button>
                      </div>
                    )}
                    {(inc.status === 'ABERTO' || inc.status === 'EM_ANALISE') && (
                      <button
                        type="button"
                        onClick={() => void abrirChatIncidente(inc)}
                        disabled={abrindoChat === inc.id || !inc.aluno?.id}
                        title="Abre Atendimento (Pais) com o responsável e pré-preenche a mensagem"
                        style={{
                          padding: '6px 12px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600,
                          background: '#22c55e', color: '#fff', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                          whiteSpace: 'nowrap' as const,
                          opacity: abrindoChat === inc.id || !inc.aluno?.id ? 0.65 : 1,
                        }}
                      >
                        <MessageSquare size={12} />
                        {abrindoChat === inc.id ? 'Abrindo…' : 'Resolver'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  borderRadius: 14, background: 'var(--bg-card, #fff)', padding: 16,
  border: '1px solid var(--border-color, #e5e7eb)',
};
